import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Modal from '../components/Modal';
import { getAssetPath } from '../utils/assetHelper';
import { useUser } from '../context/UserContext';
import './SettingsProfilePage.scss';

const SettingsProfilePage: React.FC = () => {
  // Get user data from context
  const { userProfile, characters, isLoading } = useUser();
  const currentCharacter = characters && characters.length > 0 ? characters[0] : null;

  // State for modals
  const [editProfileModalOpen, setEditProfileModalOpen] = useState(false);
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);
  const [disconnectService, setDisconnectService] = useState('');

  // State for form data
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    avatar: getAssetPath('assets/images/avatar-2.png')
  });

  // State for social accounts
  const [socialAccounts, setSocialAccounts] = useState({
    discord: false,
    twitter: false,
    google: false
  });

  // File input ref
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get avatar image based on character type
  const getAvatarImage = () => {
    if (!currentCharacter) return 'assets/images/avatar-1.png';
    
    switch (currentCharacter.type) {
      case 'Phantom':
        return 'assets/images/avatar-2.png';
      case 'Dark Cleric':
        return 'assets/images/character-2.png';
      default:
        return 'assets/images/avatar-1.png';
    }
  };

  // Update profile data when userProfile is loaded
  useEffect(() => {
    if (userProfile) {
      setProfileData({
        name: currentCharacter?.name || 'Adventurer',
        email: userProfile.email || 'name@domain.com',
        avatar: getAssetPath(getAvatarImage())
      });

      // For demonstration, we'll initialize social accounts to false
      // In a real app, this would likely come from the API
      setSocialAccounts({
        discord: false,
        twitter: false,
        google: false
      });
    }
  }, [userProfile, currentCharacter]);

  // Handle profile edit form submission
  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setEditProfileModalOpen(false);
    setSuccessModalOpen(true);
    
    // Auto close success modal after 3 seconds
    setTimeout(() => {
      setSuccessModalOpen(false);
    }, 3000);
  };

  // Handle profile data change
  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  // Handle avatar selection
  const handleAvatarSelect = (avatar: string) => {
    setProfileData(prev => ({
      ...prev,
      avatar
    }));
    setAvatarModalOpen(false);
  };

  // Handle file upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          setProfileData(prev => ({
            ...prev,
            avatar: event.target?.result as string
          }));
          setAvatarModalOpen(false);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle social account toggle
  const handleSocialToggle = (service: keyof typeof socialAccounts) => {
    if (socialAccounts[service]) {
      // If already connected, show disconnect modal
      setDisconnectService(service);
      setDisconnectModalOpen(true);
    } else {
      // If not connected, toggle it on
      setSocialAccounts(prev => ({
        ...prev,
        [service]: true
      }));
    }
  };

  // Handle disconnect confirmation
  const handleDisconnectConfirm = () => {
    setSocialAccounts(prev => ({
      ...prev,
      [disconnectService]: false
    }));
    setDisconnectModalOpen(false);
  };

  // Initialize tooltips on component mount
  useEffect(() => {
    // Set document title
    document.title = 'Settings - Profile | Phantom Clash';
    
    // Initialize tooltips if Bootstrap is available
    if ((window as any).bootstrap) {
      const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
      tooltipTriggerList.map(function(tooltipTriggerEl) {
        return new (window as any).bootstrap.Tooltip(tooltipTriggerEl);
      });
    }
  }, []);

  // Show loading state while user data is being fetched
  if (isLoading) {
    return <div className="loading-indicator">Loading user data...</div>;
  }

  return (
    <>
      {/* Page Menu */}
      <div className="c-page-menu">
        <ul>
          <li className="active"><Link to="/settings-profile">Profile</Link></li>
          <li><Link to="/settings-notifications">Notifications</Link></li>
          <li><Link to="/settings-wallet">Wallet</Link></li>
          <li><Link to="/settings-security">Security</Link></li>
          <li><Link to="/settings-privacy">Privacy</Link></li>
          <li><Link to="/settings-display">Theme & Display</Link></li>
          <li><Link to="/settings-advanced">Advanced Settings</Link></li>
        </ul>
      </div>

      <div className="d-block w-100">
        {/* Section Title */}
        <div className="c-section-title mt-0">
          <div className="d-flex flex-column justify-content-center">
            <h1>
              <img src={getAssetPath("assets/images/user.svg")} alt="" />
              Profile Management
            </h1>
            <p className="c-section-title__desc">Update your profile details and personalize your experience</p>
          </div>
        </div>

        {/* Profile Cover */}
        <div className="p-settings-profile__header box">
          <div 
            className="p-settings-profile__header-cover" 
            style={{ backgroundImage: `url('${getAssetPath("assets/images/profile-cover-default.jpg")}')` }}
          ></div>
          <div className="p-settings-profile__header-bottom">
            <div className="c-avatar">
              <div>
                <img src={profileData.avatar} alt="User avatar" />
              </div>
              <span className="c-avatar__stats online"></span>
            </div>
            <div className="p-settings-profile__header-info">
              <span>
                <h4>{profileData.name}</h4>
                <small><i className="bi bi-envelope-fill"></i> {profileData.email}</small>
              </span>
              <a href="#" className="c-i-button" onClick={(e) => { e.preventDefault(); setEditProfileModalOpen(true); }}>
                <i className="bi bi-pencil-square"></i>
              </a>
            </div>
          </div>
        </div>
        
        {/* Social Accounts */}
        <div className="box position-relative z-1 overflow-hidden mt-2">
          <div className="c-content-box c-content-box--xspadding c-content-box--setting-width">
            <div className="c-content-box__title">
              <h1 style={{ backgroundImage: 'none' }}><i className="bi bi-person-circle"></i> Social Accounts</h1>
              <div className="c-content-box__title-info"></div>
            </div>
            <div className="c-content-box__content">
              <h5 className="c-content-mini-title gray">
                Connect your social accounts for a more integrated experience 
                <i 
                  className="bi bi-question-circle-fill ms-md-3 ms-2" 
                  data-bs-toggle="tooltip" 
                  data-bs-placement="top" 
                  data-bs-title="Linking accounts allows you to join community events and share achievements easily."
                ></i>
              </h5>
              <div className="c-content-box__checkboxlist">
                <div className="c-content-box__checkboxlist-item c-divider">
                  <span><i className="bi bi-discord" style={{ color: '#6A44FF' }}></i> Discord</span>
                  <label className="c-switch" id="discord">
                    <input 
                      type="checkbox" 
                      className="c-switch-input" 
                      checked={socialAccounts.discord}
                      onChange={() => handleSocialToggle('discord')}
                      aria-label="Connect Discord account"
                    />
                    <span className="c-switch-label" data-on="On" data-off="Off"></span>
                    <span className="c-switch-handle"></span>
                  </label>
                </div>
                <div className="c-content-box__checkboxlist-item c-divider">
                  <span><i className="bi bi-twitter-x" style={{ color: 'white' }}></i> X / Twitter</span>
                  <label className="c-switch" id="twitter">
                    <input 
                      type="checkbox" 
                      className="c-switch-input" 
                      checked={socialAccounts.twitter}
                      onChange={() => handleSocialToggle('twitter')}
                      aria-label="Connect Twitter account"
                    />
                    <span className="c-switch-label" data-on="On" data-off="Off"></span>
                    <span className="c-switch-handle"></span>
                  </label>
                </div>
                <div className="c-content-box__checkboxlist-item c-divider pb-2">
                  <span><i><img src={getAssetPath("assets/images/google.svg")} alt="Google" /></i> Google</span>
                  <label className="c-switch" id="google">
                    <input 
                      type="checkbox" 
                      className="c-switch-input" 
                      checked={socialAccounts.google}
                      onChange={() => handleSocialToggle('google')}
                      aria-label="Connect Google account"
                    />
                    <span className="c-switch-label" data-on="On" data-off="Off"></span>
                    <span className="c-switch-handle"></span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      <Modal 
        isOpen={editProfileModalOpen} 
        onClose={() => setEditProfileModalOpen(false)}
        title="Edit Profile"
        isLargeWidth={true}
        className="edit-profile-modal"
        icon={<i className="bi bi-person-circle"></i>}
      >
        <div className="px-3">
          <div className="row">
            <div className="col-md-3 col-12 mb-md-0 mb-3 d-md-block d-flex justify-content-md-start justify-content-center">
              <div className="c-avatar">
                <div>
                  <img src={profileData.avatar} alt="User avatar" />
                </div>
                <span 
                  className="c-avatar__edit" 
                  onClick={() => {
                    setEditProfileModalOpen(false);
                    setAvatarModalOpen(true);
                  }}
                >
                  <i className="bi bi-camera-fill"></i>
                </span>
              </div>
            </div>
            <div className="col-md-9 col-12">
              <form onSubmit={handleProfileSubmit}>
                <div className="c-input-group mb-md-4 mb-3">
                  <label htmlFor="name" className="c-form-label c-form-label--lg">Name</label>
                  <div className="c-input-floating">
                    <input 
                      type="text" 
                      className="c-form-control"
                      id="name" 
                      value={profileData.name}
                      onChange={handleProfileChange}
                    />
                    <i className="bi bi-person-fill"></i>
                  </div>
                  <div className="c-form-error"></div>
                </div>
                <div className="c-input-group mb-4">
                  <label htmlFor="email" className="c-form-label c-form-label--lg">E-mail</label>
                  <div className="c-input-floating">
                    <input 
                      type="email" 
                      className="c-form-control"
                      id="email" 
                      value={profileData.email}
                      onChange={handleProfileChange}
                    />
                    <i className="bi bi-at"></i>
                  </div>
                  <div className="c-form-error"></div>
                </div>
                <button type="submit" className="c-button">Apply the changes</button>
              </form>
            </div>
          </div>
        </div>
      </Modal>

      {/* Avatar Modal */}
      <Modal 
        isOpen={avatarModalOpen} 
        onClose={() => setAvatarModalOpen(false)}
        isLargeWidth={true}
        hideHeader={true}
        className="avatar-modal"
      >
        <div className="py-4 px-3">
          <h1 className="c-divider-title">
            <span>Choose from the <small>collection</small></span>
          </h1>
          <div className="p-avatar-list">
            {[1, 2, 3, 4, 5].map((index) => (
              <div 
                key={index}
                className={`c-avatar ${profileData.avatar.includes(`avatar-${index}`) ? 'selected' : ''}`}
                onClick={() => handleAvatarSelect(getAssetPath(`assets/images/avatar-${index}.png`))}
              >
                <div>
                  <img src={getAssetPath(`assets/images/avatar-${index}.png`)} alt={`Avatar ${index}`} />
                </div>
              </div>
            ))}
          </div>
          <div className="d-flex justify-content-center mt-4">
            <button 
              className="c-button c-button--blue-bordered mt-3"
              onClick={() => setAvatarModalOpen(false)}
            >
              Save
            </button>
          </div>
          <h1 className="c-divider-title gray mb-4">
            <span>Or select from your computer</span>
          </h1>
          <div className="d-flex justify-content-center">
            <button 
              className="c-button c-button--bordered c-button--grayicon"
              onClick={() => fileInputRef.current?.click()}
            >
              <i className="bi bi-laptop"></i> Choose File
            </button>
            <input 
              type="file" 
              id="fileInput" 
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept="image/*"
              onChange={handleFileUpload}
              aria-label="Upload profile picture"
            />
          </div>
        </div>
      </Modal>

      {/* Success Modal */}
      <Modal 
        isOpen={successModalOpen} 
        onClose={() => setSuccessModalOpen(false)}
        title="Edit Profile"
        isNotification={true}
        notificationType="warning"
        pattern={true}
        isLargeWidth={true}
        className="success-modal"
        icon={<i className="bi bi-person-circle"></i>}
      >
        <div className="c-modal__notification-2">
          <div className="lottie-player" style={{ marginTop: '20px', marginBottom: '-30px' }}>
            <div className="success-animation" style={{ width: '200px', height: '200px' }}>
              <i className="bi bi-check-circle-fill" style={{ fontSize: '80px', color: '#4CAF50' }}></i>
            </div>
          </div>
          <h1>Profile data <br/>successfully updated</h1>
          <p>Profile data has been updated and saved.</p>
          <div className="c-timer mt-4 mb-md-3 mb-1">
            <div className="c-timer-bar"></div>
          </div>
        </div>
      </Modal>

      {/* Disconnect Modal */}
      <Modal 
        isOpen={disconnectModalOpen} 
        onClose={() => setDisconnectModalOpen(false)}
        title={`Disconnect ${disconnectService.charAt(0).toUpperCase() + disconnectService.slice(1)}?`}
        isNotification={true}
        notificationType="info"
        pattern={true}
        className="disconnect-modal"
        icon={<i className="bi bi-question-circle-fill"></i>}
      >
        <div className="c-modal__notification-2 mb-3">
          <div className="c-modal__disconnect">
            <i className={`bi bi-${disconnectService === 'google' ? 'google' : disconnectService}`}></i>
          </div>
          <h1>Disconnect {disconnectService.charAt(0).toUpperCase() + disconnectService.slice(1)}?</h1>
          <p>Disconnecting this account will prevent you from using it for login and sharing achievements. Are you sure you want to proceed</p>
        </div>
        <div className="row">
          <div className="col-12 d-flex justify-content-center mt-md-4 mt-3">
            <a 
              href="#" 
              className="c-button c-button--bordered"
              onClick={(e) => {
                e.preventDefault();
                handleDisconnectConfirm();
              }}
            >
              Confirm
            </a>
          </div>
          <div className="col-12 d-flex justify-content-center mt-3">
            <a 
              href="#" 
              className="c-button c-button--blue-bordered c-button--l"
              onClick={(e) => {
                e.preventDefault();
                setDisconnectModalOpen(false);
              }}
            >
              Cancel
            </a>
      </div>
    </div>
      </Modal>
    </>
  );
};

export default SettingsProfilePage; 